import { useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { rescueStalledAdoptersQuery, type RescueStalledAdoptersData } from '../lib/rescueQueries';
import { mapStalledAdopter } from '../lib/stalledAdopterAdapters';
import type { StalledAdopter } from '../lib/types';

/**
 * Every stalled adopter, for a reminder modal. The modals mount per opening, so this loads once per
 * opening, and always from the network: the list exists to show who was texted a minute ago, which a
 * cached copy would get wrong.
 *
 * `onFirstLoad` runs once, when the first answer arrives, not again for a retry or a refetch.
 */
export default function useStalledAdopters(onFirstLoad?: (adopters: StalledAdopter[]) => void) {
  const firstLoadSeen = useRef(false);
  const { data, loading, error, refetch } = useQuery<RescueStalledAdoptersData>(rescueStalledAdoptersQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (result) => {
      if (firstLoadSeen.current) {
        return;
      }
      firstLoadSeen.current = true;
      onFirstLoad?.(result.rescueStalledAdopters.adopters.map(mapStalledAdopter));
    },
  });
  const adopters = useMemo(() => (data?.rescueStalledAdopters.adopters ?? []).map(mapStalledAdopter), [data]);
  return {
    adopters,
    smsTemplate: data?.rescueStalledAdopters.smsTemplate ?? null,
    smsOptOutLine: data?.rescueStalledAdopters.smsOptOutLine ?? null,
    loading: loading && !data,
    error: data ? undefined : error,
    refetch,
  };
}
